// src/components/common/ActiveSessionsModal.jsx
import React, { useEffect, useState } from "react";
import authApi from "../../api/auth.api";

export const ActiveSessionsModal = ({ isOpen, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", type: "" });
  const [confirmingRevokeId, setConfirmingRevokeId] = useState(null);
  const [confirmingRevokeAll, setConfirmingRevokeAll] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setFeedback({ message: "", type: "" });
      const data = await authApi.getActiveSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load sessions:", err);
      setFeedback({
        message: err.response?.data?.message || "Failed to load active sessions.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleRevokeOne = async (sessionId) => {
    try {
      setRevokingId(sessionId);
      await authApi.revokeSession(sessionId);
      setFeedback({
        message: "Session terminated successfully.",
        type: "success",
      });
      setConfirmingRevokeId(null);
      await fetchSessions();
    } catch (err) {
      setFeedback({
        message: err.response?.data?.message || "Failed to revoke session.",
        type: "danger",
      });
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    try {
      setRevokingAll(true);
      await authApi.revokeAllOtherSessions();
      setFeedback({
        message: "All other sessions have been logged out.",
        type: "success",
      });
      setConfirmingRevokeAll(false);
      await fetchSessions();
    } catch (err) {
      setFeedback({
        message:
          err.response?.data?.message || "Failed to revoke other sessions.",
        type: "danger",
      });
    } finally {
      setRevokingAll(false);
    }
  };

  const parseUserAgent = (ua) => {
    if (!ua) return "Unknown Browser / Device";
    if (ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone")) {
      return `Mobile Device (${ua.slice(0, 32)}...)`;
    }
    if (ua.includes("Chrome")) return "Chrome Browser";
    if (ua.includes("Firefox")) return "Firefox Browser";
    if (ua.includes("Safari")) return "Safari Browser";
    if (ua.includes("Edge")) return "Edge Browser";
    return ua.length > 40 ? `${ua.slice(0, 40)}...` : ua;
  };

  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div
      className="modal-backdrop-custom"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 24, 39, 0.6)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        className="modal-card-custom"
        style={{
          background: "#ffffff",
          borderRadius: "14px",
          width: "100%",
          maxWidth: "580px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Active Login Sessions
              </h3>
        
            </div>
     
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              color: "#9CA3AF",
              fontSize: "18px",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Feedback Alert */}
        {feedback.message && (
          <div
            style={{
              margin: "16px 24px 0",
              padding: "10px 14px",
              borderRadius: "8px",
              fontSize: "13px",
              background: feedback.type === "success" ? "#ECFDF5" : "#FEF2F2",
              color: feedback.type === "success" ? "#065F46" : "#991B1B",
              border: `1px solid ${
                feedback.type === "success" ? "#A7F3D0" : "#FECACA"
              }`,
            }}
          >
            {feedback.message}
          </div>
        )}

        {/* Body */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "#6B7280",
                fontSize: "14px",
              }}
            >
              <div
                className="spinner-border spinner-border-sm text-primary me-2"
                role="status"
              />
              Loading active sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "#6B7280",
                fontSize: "14px",
              }}
            >
              No active sessions found.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {sessions.map((s) => {
                const isCurrent = !!s.isCurrent;
                return (
                  <div
                    key={s.sessionId || s.id}
                    style={{
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: `1px solid ${isCurrent ? "#93C5FD" : "#E5E7EB"}`,
                      background: isCurrent ? "#F8FAFC" : "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "16px",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <strong
                          style={{
                            fontSize: "14px",
                            color: "#1F2937",
                            fontWeight: 600,
                          }}
                        >
                          {parseUserAgent(s.userAgent)}
                        </strong>
                        {isCurrent && (
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              background: "#DCFCE7",
                              color: "#166534",
                              fontWeight: 600,
                            }}
                          >
                            Current Device
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6B7280",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        {s.ipAddress && (
                          <span>
                            <strong>IP:</strong> {s.ipAddress}
                          </span>
                        )}
                        <span>
                          <strong>Last Active:</strong>{" "}
                          {s.lastActiveAt
                            ? new Date(s.lastActiveAt).toLocaleString()
                            : "Just now"}
                        </span>
                      </div>
                    </div>

                    <div>
                      {!isCurrent && (
                        confirmingRevokeId === (s.sessionId || s.id) ? (
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRevokeOne(s.sessionId || s.id)}
                              disabled={revokingId === (s.sessionId || s.id)}
                              style={{
                                fontSize: "11px",
                                padding: "3px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              {revokingId === (s.sessionId || s.id) ? "..." : "Confirm"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setConfirmingRevokeId(null)}
                              style={{
                                fontSize: "11px",
                                padding: "3px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => setConfirmingRevokeId(s.sessionId || s.id)}
                            disabled={revokingId === (s.sessionId || s.id)}
                            style={{
                              fontSize: "12px",
                              padding: "4px 10px",
                              borderRadius: "6px",
                            }}
                          >
                            Revoke
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#F9FAFB",
          }}
        >
          {confirmingRevokeAll ? (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: "#DC2626", fontWeight: 500 }}>
                Log out other devices?
              </span>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleRevokeAllOthers}
                disabled={revokingAll}
                style={{ fontSize: "12px", padding: "4px 10px" }}
              >
                {revokingAll ? "Logging Out..." : "Confirm"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setConfirmingRevokeAll(false)}
                style={{ fontSize: "12px", padding: "4px 10px" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setConfirmingRevokeAll(true)}
              disabled={loading || revokingAll || otherSessionsCount === 0}
              style={{ fontSize: "13px", padding: "6px 14px" }}
            >
              Log Out All Other Devices
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onClose}
            style={{ fontSize: "13px", padding: "6px 16px" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveSessionsModal;
